/** Fonts already handed to `document.fonts` for this page. */
const registeredNames = new Set<string>();

const SYSTEM_OR_THEME = /^(?:\+m[nj]-|Arial|Calibri|Times|Georgia|Verdana|Tahoma|Comic Sans|Impact|Courier|Aptos|Segoe|Cambria|Helvetica|Wingdings|Symbol|Marlett|Ebrima|Nirmala|Estrangelo|Twemoji|맑은|新細|游ゴ|等线)/i;

const WEIGHT_SUFFIXES: Array<[RegExp, number]> = [
  [/\s+thin$/i, 100],
  [/\s+extra\s*light$/i, 200],
  [/\s+light$/i, 300],
  [/\s+(?:regular|roman)$/i, 400],
  [/\s+medium$/i, 500],
  [/\s+semi\s*bold$/i, 600],
  [/\s+(?:bold|heavy)$/i, 700],
  [/\s+extra\s*bold$/i, 800],
  [/\s+black$/i, 900],
];

/**
 * Load faces from PPTX `ppt/fonts/*.fntdata` (EOT→TTF) and, for names
 * still missing, fetch the closest Google Fonts family.
 */
export async function registerFontsFromPptx(buffer: ArrayBuffer): Promise<void> {
  if (typeof document === 'undefined' || !document.fonts) {
    return;
  }
  try {
    const JSZip = (await import('jszip')).default;
    const zip = await JSZip.loadAsync(buffer);
    const named = new Set<string>();
    const loaders: Array<Promise<void>> = [];

    for (const [path, file] of Object.entries(zip.files)) {
      if (file.dir) {
        continue;
      }
      if (/^ppt\/fonts\/.+\.(fntdata|odttf|ttf|otf)$/i.test(path)) {
        loaders.push(
          file.async('uint8array').then((bytes) => registerEmbeddedFont(bytes)).catch(() => undefined),
        );
      }
      if (/^ppt\/(slides|slideMasters|slideLayouts)\//i.test(path) && path.endsWith('.xml')) {
        loaders.push(
          file.async('string').then((xml) => collectTypefaces(xml, named)).catch(() => undefined),
        );
      }
    }

    await Promise.all(loaders);

    const missing = [...named].filter((name) => !registeredNames.has(name.toLowerCase()) && !isLoaded(name));
    await Promise.all(missing.map((name) => loadFromGoogleFonts(name).catch(() => undefined)));
    await document.fonts.ready;
  } catch {
    /* viewer still renders with fallbacks */
  }
}

function collectTypefaces(xml: string, into: Set<string>): void {
  const re = /typeface="([^"]+)"/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(xml))) {
    const name = match[1].trim();
    if (name && !SYSTEM_OR_THEME.test(name)) {
      into.add(name);
    }
  }
}

async function registerEmbeddedFont(bytes: Uint8Array): Promise<void> {
  const sfnt = extractSfnt(bytes);
  if (!sfnt) {
    return;
  }
  const names = parseTtfNames(sfnt);
  if (!names.length) {
    return;
  }
  const copy = sfnt.slice();
  for (const name of names) {
    await addFace(name, copy);
  }
}

function extractSfnt(data: Uint8Array): Uint8Array | null {
  const magics = [
    [0x00, 0x01, 0x00, 0x00],
    [0x4f, 0x54, 0x54, 0x4f],
    [0x74, 0x72, 0x75, 0x65],
  ];
  const limit = Math.min(data.length - 4, 2048);
  for (let i = 0; i < limit; i++) {
    for (const magic of magics) {
      if (
        data[i] === magic[0] &&
        data[i + 1] === magic[1] &&
        data[i + 2] === magic[2] &&
        data[i + 3] === magic[3]
      ) {
        return data.subarray(i);
      }
    }
  }
  return null;
}

function parseTtfNames(sfnt: Uint8Array): string[] {
  if (sfnt.length < 12) {
    return [];
  }
  const view = new DataView(sfnt.buffer, sfnt.byteOffset, sfnt.byteLength);
  const numTables = view.getUint16(4, false);
  let nameOff = 0;
  for (let i = 0; i < numTables; i++) {
    const rec = 12 + i * 16;
    const tag = String.fromCharCode(sfnt[rec], sfnt[rec + 1], sfnt[rec + 2], sfnt[rec + 3]);
    if (tag === 'name') {
      nameOff = view.getUint32(rec + 8, false);
      break;
    }
  }
  if (!nameOff || nameOff + 6 > sfnt.length) {
    return [];
  }
  const count = view.getUint16(nameOff + 2, false);
  const stringOff = view.getUint16(nameOff + 4, false);
  const names = new Set<string>();
  for (let i = 0; i < count; i++) {
    const rec = nameOff + 6 + i * 12;
    if (rec + 12 > sfnt.length) {
      break;
    }
    const platform = view.getUint16(rec, false);
    const nameId = view.getUint16(rec + 6, false);
    if (nameId !== 1 && nameId !== 4 && nameId !== 16) {
      continue;
    }
    const length = view.getUint16(rec + 8, false);
    const offset = view.getUint16(rec + 10, false);
    const start = nameOff + stringOff + offset;
    const bytes = sfnt.subarray(start, start + length);
    let text = '';
    if (platform === 0 || platform === 3) {
      for (let j = 0; j + 1 < bytes.length; j += 2) {
        text += String.fromCharCode((bytes[j] << 8) | bytes[j + 1]);
      }
    } else {
      text = Array.from(bytes, (b) => String.fromCharCode(b)).join('');
    }
    text = text.replace(/\0/g, '').trim();
    if (text && !SYSTEM_OR_THEME.test(text)) {
      names.add(text);
    }
  }
  return [...names];
}

async function loadFromGoogleFonts(pptxName: string): Promise<void> {
  if (isLoaded(pptxName)) {
    registeredNames.add(pptxName.toLowerCase());
    return;
  }
  let family = pptxName;
  let weight = 400;
  for (const [re, w] of WEIGHT_SUFFIXES) {
    if (re.test(family)) {
      weight = w;
      family = family.replace(re, '').trim();
      break;
    }
  }
  if (!family || SYSTEM_OR_THEME.test(family)) {
    return;
  }
  const cssUrl =
    'https://fonts.googleapis.com/css2?family=' +
    encodeURIComponent(family).replace(/%20/g, '+') +
    ':wght@' +
    weight +
    '&display=swap';
  const css = await fetchWithTimeout(cssUrl, 4000);
  if (!css) {
    return;
  }
  const src = css.match(/url\(([^)]+)\)/);
  if (!src) {
    return;
  }
  const url = src[1].replace(/['"]/g, '');
  await addFace(pptxName, `url(${url})`);
  if (family !== pptxName) {
    await addFace(family, `url(${url})`);
  }
}

async function addFace(family: string, source: Uint8Array | string): Promise<void> {
  const key = family.toLowerCase();
  if (!family || registeredNames.has(key)) {
    return;
  }
  try {
    const face = new FontFace(family, typeof source === 'string' ? source : source.slice());
    await face.load();
    (document.fonts as FontFaceSet & { add(font: FontFace): void }).add(face);
    registeredNames.add(key);
  } catch {
    /* ignore a single face */
  }
}

function isLoaded(name: string): boolean {
  try {
    return document.fonts.check(`12px "${name}"`);
  } catch {
    return false;
  }
}

async function fetchWithTimeout(url: string, ms: number): Promise<string | null> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = setTimeout(() => ctrl?.abort(), ms);
  try {
    const res = await fetch(url, { signal: ctrl?.signal });
    if (!res.ok) {
      return null;
    }
    return await res.text();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}
